import React, { useEffect, useState } from 'react';
import api from '../../services/api';

type ContactStatus = 'new' | 'read' | 'archived';

interface ContactRequest {
  id: number;
  name: string;
  email: string;
  message: string;
  status: ContactStatus;
  source?: string;
  createdAt?: string;
}

const statusFilterOptions: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'read', label: 'Read' },
  { value: 'archived', label: 'Archived' },
];

const statusStyles: Record<ContactStatus, string> = {
  new: 'bg-primary-50 text-primary-700 ring-primary-100',
  read: 'bg-secondary-50 text-secondary-700 ring-secondary-100',
  archived: 'bg-neutral-100 text-neutral-600 ring-neutral-200',
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return value;
  }
};

const ContactRequests: React.FC = () => {
  const [requests, setRequests] = useState<ContactRequest[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = statusFilter ? { status: statusFilter } : undefined;
      const res = await api.get<ContactRequest[]>('/api/platform/contact-requests', { params });
      setRequests(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError('Unable to load contact requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadRequests();
    // Reload when filter changes; loadRequests closes over statusFilter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const updateStatus = async (id: number, status: ContactStatus) => {
    try {
      setUpdatingId(id);
      await api.patch(`/api/platform/contact-requests/${id}`, { status });
      setRequests((prev) =>
        prev.map((req) => (req.id === id ? { ...req, status } : req))
      );
    } catch {
      setError('Unable to update contact request.');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">Contact requests</h1>
          <p className="mt-1 text-sm text-neutral-500">Leads from the landing page and other sources.</p>
        </div>

        <div>
          <label htmlFor="status-filter" className="block text-xs font-medium text-neutral-600 mb-1">
            Filter by status
          </label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
          >
            {statusFilterOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-[3px] border-neutral-200 border-t-primary-500" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200 text-sm">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Message
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {requests.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-neutral-500">
                      No contact requests found.
                    </td>
                  </tr>
                ) : (
                  requests.map((req) => (
                    <tr key={req.id} className="hover:bg-neutral-50/80 align-top">
                      <td className="px-4 py-3 text-neutral-600 whitespace-nowrap">
                        {formatDate(req.createdAt)}
                      </td>
                      <td className="px-4 py-3 font-medium text-neutral-900">{req.name}</td>
                      <td className="px-4 py-3 text-neutral-600">
                        <a href={`mailto:${req.email}`} className="hover:text-primary-600">
                          {req.email}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-neutral-600 max-w-xs">
                        <p className="line-clamp-2">{req.message}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ring-1 ${statusStyles[req.status]}`}
                        >
                          {req.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex gap-2">
                          {req.status !== 'read' && (
                            <button
                              type="button"
                              disabled={updatingId === req.id}
                              onClick={() => updateStatus(req.id, 'read')}
                              className="rounded-lg border border-neutral-300 px-2.5 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
                            >
                              Mark read
                            </button>
                          )}
                          {req.status !== 'archived' && (
                            <button
                              type="button"
                              disabled={updatingId === req.id}
                              onClick={() => updateStatus(req.id, 'archived')}
                              className="rounded-lg border border-neutral-300 px-2.5 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
                            >
                              Archive
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactRequests;
